import React, { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { changePassword } from "../../api/user/userService";

const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleSaveChanges = async () => {
    setErrors({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    let hasError = false;
    const newErrors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!currentPassword) {
      newErrors.currentPassword = t("alert.error.required");
      hasError = true;
    }

    if (!newPassword) {
      newErrors.newPassword = t("alert.error.required");
      hasError = true;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t("alert.error.required");
      hasError = true;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t("alert.error.passwordsDoNotMatch");
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true); // Set loading state to true before API call

    try {
      const passwordData = {
        oldPassword: currentPassword,
        newPassword: newPassword,
      };

      await changePassword(passwordData);
      toast.success(
        t("alert.passwordChanged", "Password changed successfully")
      );
      // Small delay to allow toast to be seen before navigation
      setTimeout(() => {
        navigate("/profile");
      }, 500);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to change password. Please try again.";
      toast.error(errorMessage);
      setIsLoading(false); // Reset loading state on error
    }
  };

  return (
    <Box className="flex flex-col bg-white h-[75vh] md:h-[80vh] ">
      <Box className="flex-1 pt-1 mx-auto w-full sm:max-w-md md:max-w-xl lg:max-w-2xl xl:max-w-4xl">
        <Box className="p-2 flex items-center ">
          <IconButton
            onClick={() => navigate("/profile")}
            className="mr-4"
            size="medium"
          >
            <ArrowLeft size={24} />
          </IconButton>
          <Typography variant="h6" className="font-semibold">
            {t("button.changePassword")}
          </Typography>
        </Box>
        {/* Form */}
        <Box className="p-4 flex-grow">
          <div className="space-y-6 max-w-md mx-auto">
            {/* Current Password */}
            <div>
              <label
                htmlFor="current-password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("label.passwordCurrent")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    if (errors.currentPassword && e.target.value) {
                      setErrors({ ...errors, currentPassword: "" });
                    }
                  }}
                  className={`w-full py-3 px-4 border ${errors.currentPassword ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff size={20} className="text-gray-400" />
                  ) : (
                    <Eye size={20} className="text-gray-400" />
                  )}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.currentPassword}
                </p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("label.passwordNew")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (errors.newPassword && e.target.value) {
                      setErrors({ ...errors, newPassword: "" });
                    }
                  }}
                  className={`w-full py-3 px-4 border ${errors.newPassword ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff size={20} className="text-gray-400" />
                  ) : (
                    <Eye size={20} className="text-gray-400" />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("label.passwordConfirm")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword && e.target.value) {
                      setErrors({ ...errors, confirmPassword: "" });
                    }
                  }}
                  className={`w-full py-3 px-4 border ${errors.confirmPassword ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={20} className="text-gray-400" />
                  ) : (
                    <Eye size={20} className="text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>
        </Box>

        {/* Action Buttons */}
        <Box className="p-4 ">
          <div className="flex gap-3 max-w-md mx-auto">
            <button
              onClick={() => navigate("/profile")}
              className="flex-1 py-3 px-4 bg-orange-50 text-gray-700 rounded-lg font-bold"
              disabled={isLoading}
            >
              {t("button.cancel", "Cancel")}
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={isLoading}
              className="flex-1 py-3 px-4 bg-orange-500 text-white rounded-lg font-bold flex justify-center items-center"
            >
              {isLoading ? (
                <>
                  <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("label.processing")}
                </>
              ) : (
                t("label.saveChanges")
              )}
            </button>
          </div>
        </Box>
      </Box>
      {/* Header with back button */}
    </Box>
  );
};

export default ChangePasswordPage;
